import { HttpClient } from "@angular/common/http";
import { UntypedFormGroup } from "@angular/forms";
import { Observable } from "rxjs";
import { debounceTime, filter, take } from "rxjs/operators";
import { DestroyBase } from "../ng-destroy-base";
import { AppServiceHelperService } from "../../../services/app-service-helper.service";
import { EventBusService } from "../../../services/event-bus.service";

/**
 * Search form base class
 */
export abstract class SearchFormBase<T> extends DestroyBase {
  /**
   * RefList API Resource
   */
  private _reflistResource: string;

  /**
 * User API Resource
 */
  private _userResource: string;

  /**
  * FormGroup that holds the data, validation etc
  */
  private _formData: UntypedFormGroup = null;

  /**
   * FormGroup that holds the data, validation etc
   */
  public get formData(): UntypedFormGroup {
    return this._formData;
  }

  /**
   * Holds reflist data. It can be accessed via this.reflistData.ref_list_name or this.reflistData['ref_list_name']
   */
  private _reflistData: any = null;

  /**
   * Holds reflist data. It can be accessed via this.reflistData.ref_list_name or this.reflistData['ref_list_name']
   */
  public get reflistData(): any {
    return this._reflistData;
  }

  /**
   * Indicates whether the form is ready to be rendered. Please guard the form rendering with *ngIf and show a loading indicator if the value is false
   */
  private _formReady = false;

  /**
   * Indicates whether the form is ready to be rendered. Please guard the form rendering with *ngIf and show a loading indicator if the value is false
   */
  public get formReady(): boolean {
    return this._formReady;
  }

  /**
   * Indicates whether the form is savin. Please show a loading indicator if the value is true and set the save trigger controls to disbled
   */
  private _saving = false;

  /**
   * Indicates whether the form is savin. Please show a loading indicator if the value is true and set the save trigger controls to disbled
   */
  public get saving(): boolean {
    return this._saving;
  }

  /**
   * Registers, which ref lists should be loaded
   */
  private _reflistsToLoad: string[] = [];

  /**
   * Is search form reset in progress?
   */
  private _searchFormResetInProgress = false;

  /**
   * Constructor
   * @param httpClient HttpClient
   */
  constructor(protected eventBus: EventBusService, protected httpClient: HttpClient, protected resetEventName: string, private appServiceHelper: AppServiceHelperService) {
    super();
    this._reflistResource = appServiceHelper.getServiceEndpoint('pollux.api', 'reflist/pollux');
    this._userResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist/pollux/user');
  }

  /**
   * The search event name (event bus)
   */
  protected abstract get searchEventName(): string;

  /**
   * The show all event name (event bus)
   */
  protected abstract get showAllEventName(): string;

  /**
   * Gets a filter object
   * @param form The form
   */
  protected abstract getFilterObject(form: UntypedFormGroup): any;

  /**
   * Creates the FormGroup and populates it with the data
   * @param data Loaded data
   */
  protected abstract createFormGroup(data: T): UntypedFormGroup;

  /**
   * Use this if you don't need load post-processing
   * @param data
   * @returns Promise
   */
  protected noProcessing(data: T): Promise<T> {
    return Promise.resolve(data);
  }

  /**
   * Initializes the form
   * @returns A Promise that should be awaited
   */
  protected init(): Promise<void> {
    if (this.resetEventName) {
      this.subscriptions.add(this.eventBus.subscribeEvent(this.resetEventName, (_payload, _options) => {
        this.showAll();
      }));
    }

    this._formReady = false;
    return new Promise<void>((resolve, reject) => {
      this.loadReflists()
        .then(() => this.initFormData())
        .then((data) => {
          this._formReady = true;
          this._formData = this.createFormGroup(data);

          const searchChanged$ = this._formData.valueChanges
            .pipe(
              filter(() => !this._searchFormResetInProgress),//don't fire if value changes because of reset
              debounceTime(500)
            )
            .subscribe(() => {
              const filter = this.getFilterObject(this._formData);
              this.eventBus.broadcastEvent(this.searchEventName, filter);
            });

          this.subscriptions.add(searchChanged$);
          resolve();
        }).catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Show all
   */
  public showAll(): void {
    this._searchFormResetInProgress = true;
    this.reset();
    this._searchFormResetInProgress = false;

    this.eventBus.broadcastEvent(this.showAllEventName);
  }

  /**
   * Resets the form
   */
  protected abstract reset(): void;

  /**
   * Requests a reflist to be loaded. Must be called before init() is called
   * @param reflistName Name of the reflist
   */
  protected requireReflist(reflistName: string): void {
    if (this._reflistsToLoad.indexOf(reflistName) < 0) {
      this._reflistsToLoad.push(reflistName);
    }
  }

  /**
   * Loads the data. 
   */
  protected abstract initFormData(): Promise<T>;

  /**
   * Loads user
   */
  public loadUsers(): Observable<any> {
    const url = `${this._userResource}`;
    return this.httpClient.get(url);
  }

  /**
   * Loads a reflist
   * @param name The reflist name (ref_xyz)
   */
  private loadReflist(name: string): Promise<any> {
    const url = `${this._reflistResource}?reflistName=${name}&withParentNo=false`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Loads the reflists
   * @returns
   */
  private async loadReflists(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const promises = [];

      if (this._reflistsToLoad.length === 0) {
        resolve();
      }
      else {
        for (const reflistToLoad of this._reflistsToLoad) {
          promises.push(this.loadReflist(reflistToLoad));
        }

        Promise.all(promises).then((reflistResult: any[]) => {
          const reflistData = {};

          for (let i = 0; i < this._reflistsToLoad.length; ++i) {
            reflistData[this._reflistsToLoad[i]] = this.processReflistResult(this._reflistsToLoad[i], reflistResult[i]);
          }

          this._reflistData = reflistData;
          resolve();
        }).catch((error) => {
          reject(error);
        });
      }
    });
  }

  /**
   * Allows processing / filtering / sorting of reflists
   * @param reflistName The reflist name
   * @param reflistData The reflist data
   * @returns The filtered reflist data
   */
  protected processReflistResult(reflistName: string, reflistData: any[]): any[] {
    return reflistData;
  }

  /**
   * Adds a filter entry to the filter
   * @param filter Filter
   * @param form Form
   * @param fieldNameOnForm Field name (on form)
   * @param fieldNameInFilter Field name in filter (DTO)
   * @param operator Filter operator
   */
  protected addFilter(filter: any, form: UntypedFormGroup, fieldNameOnForm: string, fieldNameInFilter: string, operator: string): void {
    if (form.get(fieldNameOnForm).value != '' && form.get(fieldNameOnForm).value != null) {
      filter.filters.push({ field: fieldNameInFilter, value: form.get(fieldNameOnForm).value, operator: operator });
      sessionStorage.setItem(fieldNameOnForm, form.get(fieldNameOnForm).value);
    } else {
      sessionStorage.removeItem(fieldNameOnForm);
    }
  }

  /**
   * Adds a filter entry to the filter
   * @param filter Filter
   * @param form Form
   * @param fieldNameOnForm Field name (on form)
   * @param fieldNameInFilter Field name in filter (DTO)
   * @param operator Filter operator
   */
  protected addFilterNoStorage(filter: any, form: UntypedFormGroup, fieldNameOnForm: string, fieldNameInFilter: string, operator: string): void {
    if (form.get(fieldNameOnForm).value != '' && form.get(fieldNameOnForm).value != null) {
      filter.filters.push({ field: fieldNameInFilter, value: form.get(fieldNameOnForm).value, operator: operator });
    }
  }
}
